/**
 * Address line processing for the SEP-9 payment path.
 *
 * Core (MGI backend) enforces strict address constraints:
 *   - line1 must be 5–30 characters (raised from 4 in June 2026 after Core
 *     rejections of 4-char lines)
 *   - line2 must be ≤ 80 characters
 *
 * A comma-split was added in October 2025 to handle long Guatemala-style
 * addresses (e.g. "4ta Calle 5-21, Zona 1") that overflow line1 and would
 * otherwise be rejected by Core. The original implementation lacked a length
 * guard, so it incorrectly split short addresses that merely contained a comma
 * (e.g. "121, AV LUBWE" → line1="121"). These helpers complete the original
 * intent: only restructure when line1 actually exceeds the limit.
 */

export const ADDRESS_LINE1_MIN = 5;
export const ADDRESS_LINE1_MAX = 30;
export const ADDRESS_LINE2_MAX = 80;

/**
 * Find the rightmost comma in `value` whose preceding text (after trim) is a
 * valid line1 of 5–30 characters. Returns the resulting line1/line2 split, or
 * null when no comma produces a valid line1 (caller should fall back).
 *
 * Scanning left-to-right and overwriting `best` naturally yields the rightmost
 * valid comma, which maximizes line1 length.
 */
function findBestCommaSplit(
	value: string,
): { line1: string; line2: string } | null {
	let best: { line1: string; line2: string } | null = null;

	for (let i = 0; i < value.length; i++) {
		if (value[i] !== ",") continue;

		const head = value.slice(0, i).trim();
		if (head.length >= ADDRESS_LINE1_MIN && head.length <= ADDRESS_LINE1_MAX) {
			best = { line1: head, line2: value.slice(i + 1).trim() };
		}
	}

	return best;
}

/**
 * Shape SEP-9 address lines so they satisfy Core's length limits without losing
 * data. Rules, in priority order:
 *   0. line1 < 5 chars (after trim) → blank line1 so the address step is
 *      shown again; Core would reject it with 4103 anyway.
 *   1. line1 ≤ 30 chars → keep it intact (commas included); clamp line2 to 80.
 *   2. line1 > 30 with an existing line2 → hard-clamp line1 to 30, leave line2
 *      untouched (only clamp it to 80). Never clobber existing line2 data.
 *   3. line1 > 30 with no line2 → split at the rightmost comma that leaves a
 *      5–30 char line1, moving the remainder to line2. If no such comma exists,
 *      hard-cut line1 at 30 and preserve the overflow on line2.
 *
 * Pure function — safe in the browser/iframe SDK context.
 */
export function processAddressLines(
	line1?: string,
	line2?: string,
): { line1: string; line2: string } {
	const rawLine1 = (line1 ?? "").trim();
	const existingLine2 = (line2 ?? "").trim();

	// 0. Too short for Core (e.g. "Reno", stored after Core trimmed a "Reno "
	// that slipped past the UI). Blank it so the flow collects a real address
	// instead of failing later with error 4103.
	if (rawLine1.length < ADDRESS_LINE1_MIN) {
		return { line1: "", line2: existingLine2.slice(0, ADDRESS_LINE2_MAX) };
	}

	// 1. Already fits — keep line1 whole (a comma here is valid address data).
	if (rawLine1.length <= ADDRESS_LINE1_MAX) {
		return {
			line1: rawLine1,
			line2: existingLine2.slice(0, ADDRESS_LINE2_MAX),
		};
	}

	// 2. line1 too long but line2 already populated — clamp line1, keep line2.
	if (existingLine2) {
		return {
			line1: rawLine1.slice(0, ADDRESS_LINE1_MAX),
			line2: existingLine2.slice(0, ADDRESS_LINE2_MAX),
		};
	}

	// 3. line1 too long, no line2 — prefer a natural comma break.
	const commaSplit = findBestCommaSplit(rawLine1);
	if (commaSplit) {
		return {
			line1: commaSplit.line1,
			line2: commaSplit.line2.slice(0, ADDRESS_LINE2_MAX),
		};
	}

	// Fallback: hard cut at 30, preserving the overflow on line2 (never dropped).
	// NOTE: word-boundary truncation could be added here later as a polish
	// enhancement; we keep a simple slice for now.
	return {
		line1: rawLine1.slice(0, ADDRESS_LINE1_MAX),
		line2: rawLine1.slice(ADDRESS_LINE1_MAX).trim().slice(0, ADDRESS_LINE2_MAX),
	};
}
